/**
 * TemplateHeaderActions — botones contextuales del header del Designer.
 * (B.6.d.8.b/c/d)
 *
 * Renderiza distintas acciones según el estado del template + versión:
 *   - system-default              → [Clonar a custom]
 *   - custom + versión draft      → [Publicar] [Archivar]
 *   - custom + versión publicada  → [Nueva versión] [Archivar]
 *   - archived                    → (sin botones)
 *
 * Cada acción abre un confirm modal con el contexto + dispara la
 * mutación. Las mutaciones invalidan las queries relevantes para que
 * el Designer recargue automáticamente.
 *
 * El error 422 TEMPLATE_NOT_PUBLISHABLE devuelve los hallazgos `blocking`
 * del reporte de calidad (`details.findings`, OT-0006); se muestran
 * traducidos en un modal específico — el user ve qué falta.
 */

import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  AlertCircle,
  Archive,
  ArchiveRestore,
  Copy,
  FileText,
  Loader2,
  Send,
  Undo2,
} from 'lucide-react'

import { ApiError } from '../../../api/client'
import {
  archiveTemplate,
  cloneTemplate,
  createVersion,
  discardDraftVersion,
  listVersions,
  publishVersion,
  restoreTemplate,
  type QualityFinding,
  type Template,
  type TemplateVersionWithDefinition,
} from '../../../api/templates'
import { useAccessToken } from '../../../auth/useAccessToken'
import { useMe } from '../../../auth/useMe'
import { canManageTenant } from '../../../lib/tiers'
import { Dialog } from '../../Dialog'
import { pushToast } from '../../../stores/toastStore'
import { MethodologyPicker } from '../methodologies/MethodologyPicker'
import { QualityFindingList } from '../quality/QualityFindingList'

interface TemplateHeaderActionsProps {
  template: Template
  version: TemplateVersionWithDefinition
}

type PendingAction =
  | 'clone'
  | 'publish'
  | 'newVersion'
  | 'archive'
  | 'restore'
  | 'discardDraft'
  | null

export function TemplateHeaderActions({
  template,
  version,
}: TemplateHeaderActionsProps) {
  const { t } = useTranslation(['templates', 'errors'])
  const { accessToken } = useAccessToken()
  const { me } = useMe()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // OT-0009 §1.2 #13 — todas las mutaciones de plantillas (clonar,
  // publicar, nueva versión, descartar, archivar, restaurar) exigen
  // admin en backend (`require_tenant_admin`). Antes se ofrecían a
  // cualquiera y el backend rechazaba al enviar. Con la organización en
  // solo lectura tampoco se ofrecen (OT-0073 §4.4).
  const canManage = canManageTenant(me?.role, me?.tenant?.access)

  const [pending, setPending] = useState<PendingAction>(null)
  const [publishErrors, setPublishErrors] = useState<QualityFinding[] | null>(null)
  // Metodología del clon (OT-0055). Se fija al abrir el diálogo: el
  // duplicado de una plantilla propia conserva la suya; el clon de una del
  // producto arranca genérico, salvo el ciclo, que exige elegir una.
  const [cloneMethodologyId, setCloneMethodologyId] = useState<string | null>(null)
  const isLifecycle = template.template_type === 'lifecycle'
  const openClone = () => {
    setCloneMethodologyId(template.tenant_id !== null ? template.methodology_id : null)
    setPending('clone')
  }

  const isSystemDefault = template.is_system_default
  const isArchived = template.status === 'archived'
  const isVersionDraft = version.published_at === null
  const isVersionPublished = version.published_at !== null

  // ¿Hay alguna versión publicada anterior? Necesario para mostrar
  // "Descartar borrador" — si no hay publicada atrás, el discard
  // fallaría con CANNOT_DISCARD_DRAFT.
  const versionsQuery = useQuery({
    queryKey: ['templates', template.id, 'versions'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listVersions(accessToken, template.id)
    },
    enabled: Boolean(accessToken) && isVersionDraft && !isSystemDefault,
    staleTime: 30_000,
  })
  const hasPriorPublished =
    (versionsQuery.data ?? []).some((v) => v.published_at !== null) === true

  // ─── Mutations ─────────────────────────────────────────────────────────
  const cloneMutation = useMutation({
    mutationFn: async () => {
      if (!accessToken) throw new Error('no_access_token')
      return cloneTemplate(
        accessToken,
        template.id,
        cloneMethodologyId ? { methodology_id: cloneMethodologyId } : {},
      )
    },
    onSuccess: (newTemplate) => {
      pushToast({
        kind: 'success',
        message: t('designer.actions_toast.cloned', { name: newTemplate.name }),
      })
      void queryClient.invalidateQueries({ queryKey: ['templates'] })
      setPending(null)
      void navigate({
        to: '/templates/$templateId',
        params: { templateId: newTemplate.id },
      })
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : 'UNKNOWN'
      pushToast({
        kind: 'critical',
        message: t(`errors:${code}` as 'errors:UNKNOWN', {
          defaultValue: t('designer.actions_toast.clone_failed'),
        }),
      })
    },
  })

  const publishMutationFn = async () => {
    if (!accessToken) throw new Error('no_access_token')
    return publishVersion(accessToken, template.id, version.id)
  }
  const publishMutation = useMutation({
    mutationFn: publishMutationFn,
    onSuccess: () => {
      pushToast({
        kind: 'success',
        message: t('designer.actions_toast.published', {
          version: version.version,
        }),
      })
      void queryClient.invalidateQueries({
        queryKey: ['templates', template.id],
      })
      void queryClient.invalidateQueries({ queryKey: ['templates'] })
      setPending(null)
    },
    onError: (err) => {
      const findings =
        err instanceof ApiError && err.code === 'TEMPLATE_NOT_PUBLISHABLE'
          ? ((err.body?.details as { findings?: QualityFinding[] } | undefined)?.findings ?? [])
          : []
      if (findings.length > 0) {
        setPublishErrors(findings)
        setPending(null)
        return
      }
      const code = err instanceof ApiError ? err.code : 'UNKNOWN'
      pushToast({
        kind: 'critical',
        message: t(`errors:${code}` as 'errors:UNKNOWN', {
          defaultValue: t('designer.actions_toast.publish_failed'),
        }),
      })
    },
  })

  const newVersionMutation = useMutation({
    mutationFn: async () => {
      if (!accessToken) throw new Error('no_access_token')
      const next = bumpMinor(version.version)
      return createVersion(accessToken, template.id, {
        version: next,
        definition: version.definition,
        change_log: t('designer.actions_toast.new_version_change_log', {
          previous: version.version,
        }),
      })
    },
    onSuccess: (newVersion) => {
      pushToast({
        kind: 'success',
        message: t('designer.actions_toast.new_version_created', {
          version: newVersion.version,
        }),
      })
      void queryClient.invalidateQueries({
        queryKey: ['templates', template.id],
      })
      setPending(null)
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : 'UNKNOWN'
      pushToast({
        kind: 'critical',
        message: t(`errors:${code}` as 'errors:UNKNOWN', {
          defaultValue: t('designer.actions_toast.new_version_failed'),
        }),
      })
    },
  })

  const archiveMutation = useMutation({
    mutationFn: async () => {
      if (!accessToken) throw new Error('no_access_token')
      return archiveTemplate(accessToken, template.id)
    },
    onSuccess: () => {
      pushToast({
        kind: 'success',
        message: t('designer.actions_toast.archived', { name: template.name }),
      })
      void queryClient.invalidateQueries({ queryKey: ['templates'] })
      setPending(null)
      void navigate({ to: '/templates' })
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : 'UNKNOWN'
      pushToast({
        kind: 'critical',
        message: t(`errors:${code}` as 'errors:UNKNOWN', {
          defaultValue: t('designer.actions_toast.archive_failed'),
        }),
      })
    },
  })

  const restoreMutation = useMutation({
    mutationFn: async () => {
      if (!accessToken) throw new Error('no_access_token')
      return restoreTemplate(accessToken, template.id)
    },
    onSuccess: () => {
      pushToast({
        kind: 'success',
        message: t('designer.actions_toast.restored', {
          name: template.name,
        }),
      })
      void queryClient.invalidateQueries({
        queryKey: ['templates', template.id],
      })
      void queryClient.invalidateQueries({ queryKey: ['templates'] })
      setPending(null)
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : 'UNKNOWN'
      pushToast({
        kind: 'critical',
        message: t(`errors:${code}` as 'errors:UNKNOWN', {
          defaultValue: t('designer.actions_toast.restore_failed'),
        }),
      })
    },
  })

  const discardDraftMutation = useMutation({
    mutationFn: async () => {
      if (!accessToken) throw new Error('no_access_token')
      return discardDraftVersion(accessToken, template.id, version.id)
    },
    onSuccess: () => {
      pushToast({
        kind: 'success',
        message: t('designer.actions_toast.draft_discarded', {
          version: version.version,
        }),
      })
      void queryClient.invalidateQueries({
        queryKey: ['templates', template.id],
      })
      void queryClient.invalidateQueries({ queryKey: ['templates'] })
      setPending(null)
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : 'UNKNOWN'
      pushToast({
        kind: 'critical',
        message: t(`errors:${code}` as 'errors:UNKNOWN', {
          defaultValue: t('designer.actions_toast.discard_failed'),
        }),
      })
    },
  })

  // ─── Botones visibles ──────────────────────────────────────────────────
  if (isArchived) {
    return (
      <>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded bg-surface-muted text-ink-subtle font-mono">
            {t('designer.actions.archived_badge')}
          </span>
          {!isSystemDefault && canManage ? (
            <ActionButton
              kind="primary"
              icon={<ArchiveRestore className="w-3.5 h-3.5" />}
              label={t('designer.actions.restore')}
              onClick={() => setPending('restore')}
            />
          ) : null}
        </div>

        {/* Restore confirm */}
        <Dialog
          open={pending === 'restore'}
          onClose={() => !restoreMutation.isPending && setPending(null)}
          title={t('designer.actions.restore_dialog_title')}
          footer={
            <>
              <button
                type="button"
                onClick={() => setPending(null)}
                disabled={restoreMutation.isPending}
                className="px-4 py-2 text-sm text-ink-muted hover:text-ink rounded-md disabled:opacity-50"
              >
                {t('designer.picker.cancel')}
              </button>
              <button
                type="button"
                onClick={() => restoreMutation.mutate()}
                disabled={restoreMutation.isPending}
                className="px-5 py-2 bg-brand hover:bg-brand/90 text-canvas text-sm font-medium rounded-md disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {restoreMutation.isPending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : null}
                {t('designer.actions.restore_dialog_confirm')}
              </button>
            </>
          }
        >
          <p className="text-sm text-ink leading-relaxed">
            {t('designer.actions.restore_dialog_body', {
              name: template.name,
            })}
          </p>
        </Dialog>
      </>
    )
  }

  // OT-0009 §1.2 #13 — clonar/publicar/nueva versión/descartar/archivar
  // son admin únicamente en backend. Ninguno de los botones de abajo
  // aplica a otro tier: se oculta el bloque completo, no cada botón.
  if (!canManage) return null

  return (
    <>
      <div className="flex items-center gap-1.5">
        {isSystemDefault ? (
          <ActionButton
            kind="primary"
            icon={<Copy className="w-3.5 h-3.5" />}
            label={t('designer.actions.clone_to_custom')}
            onClick={openClone}
          />
        ) : (
          <>
            {isVersionDraft ? (
              <ActionButton
                kind="primary"
                icon={<Send className="w-3.5 h-3.5" />}
                label={t('designer.actions.publish')}
                onClick={() => setPending('publish')}
              />
            ) : null}
            {isVersionPublished ? (
              <ActionButton
                kind="primary"
                icon={<FileText className="w-3.5 h-3.5" />}
                label={t('designer.actions.new_version')}
                onClick={() => setPending('newVersion')}
              />
            ) : null}
            {/* Discard borrador — solo cuando hay un draft activo Y
                existe una publicada anterior a la cual revertir. */}
            {isVersionDraft && hasPriorPublished ? (
              <ActionButton
                kind="ghost"
                icon={<Undo2 className="w-3.5 h-3.5" />}
                label={t('designer.actions.discard_draft')}
                onClick={() => setPending('discardDraft')}
              />
            ) : null}
            {/* Forkear el custom */}
            <ActionButton
              kind="ghost"
              icon={<Copy className="w-3.5 h-3.5" />}
              label={t('designer.actions.fork')}
              onClick={openClone}
            />
            <ActionButton
              kind="ghost"
              icon={<Archive className="w-3.5 h-3.5" />}
              label={t('designer.actions.archive')}
              onClick={() => setPending('archive')}
            />
          </>
        )}
      </div>

      {/* Clone confirm */}
      <Dialog
        open={pending === 'clone'}
        onClose={() => !cloneMutation.isPending && setPending(null)}
        title={t('designer.actions.clone_dialog_title')}
        footer={
          <>
            <button
              type="button"
              onClick={() => setPending(null)}
              disabled={cloneMutation.isPending}
              className="px-4 py-2 text-sm text-ink-muted hover:text-ink rounded-md disabled:opacity-50"
            >
              {t('designer.picker.cancel')}
            </button>
            <button
              type="button"
              onClick={() => cloneMutation.mutate()}
              disabled={cloneMutation.isPending || (isLifecycle && !cloneMethodologyId)}
              className="px-5 py-2 bg-brand hover:bg-brand/90 text-canvas text-sm font-medium rounded-md disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {cloneMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : null}
              {t('designer.actions.clone_dialog_confirm')}
            </button>
          </>
        }
      >
        <p className="text-sm text-ink leading-relaxed">
          {/* Un ciclo no sustituye al original (se elige por id): su copia
              es un ciclo más para elegir al crear proyectos. */}
          {isLifecycle
            ? t('designer.actions.clone_dialog_body_lifecycle', { name: template.name })
            : t('designer.actions.clone_dialog_body', { name: template.name })}
        </p>
        <label htmlFor="clone-methodology" className="mt-4 block text-xs font-medium text-ink-muted">
          {t('designer.actions.clone_dialog_methodology')}
        </label>
        <MethodologyPicker
          id="clone-methodology"
          value={cloneMethodologyId}
          onChange={setCloneMethodologyId}
          allowGeneric={!isLifecycle}
          allowCreate
          disabled={cloneMutation.isPending}
          className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand disabled:opacity-60"
        />
        <p className="mt-1 text-[11px] text-ink-subtle">
          {isLifecycle
            ? t('designer.actions.clone_dialog_methodology_lifecycle')
            : t('designer.actions.clone_dialog_methodology_help')}
        </p>
      </Dialog>

      {/* Publish confirm */}
      <Dialog
        open={pending === 'publish'}
        onClose={() => !publishMutation.isPending && setPending(null)}
        title={t('designer.actions.publish_dialog_title', {
          version: version.version,
        })}
        footer={
          <>
            <button
              type="button"
              onClick={() => setPending(null)}
              disabled={publishMutation.isPending}
              className="px-4 py-2 text-sm text-ink-muted hover:text-ink rounded-md disabled:opacity-50"
            >
              {t('designer.picker.cancel')}
            </button>
            <button
              type="button"
              onClick={() => publishMutation.mutate()}
              disabled={publishMutation.isPending}
              className="px-5 py-2 bg-official hover:bg-official/90 text-canvas text-sm font-medium rounded-md disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {publishMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : null}
              {t('designer.actions.publish_dialog_confirm')}
            </button>
          </>
        }
      >
        <p className="text-sm text-ink leading-relaxed">
          {t('designer.actions.publish_dialog_body')}
        </p>
        <p className="mt-2 text-xs text-ink-muted leading-relaxed">
          {t('designer.actions.publish_dialog_hint')}
        </p>
      </Dialog>

      {/* Publish errors (422 with blocking quality findings) */}
      <Dialog
        open={publishErrors !== null}
        onClose={() => setPublishErrors(null)}
        title={t('designer.actions.publish_errors_title')}
        footer={
          <button
            type="button"
            onClick={() => setPublishErrors(null)}
            className="px-5 py-2 bg-brand hover:bg-brand/90 text-canvas text-sm font-medium rounded-md"
          >
            {t('designer.actions.publish_errors_close')}
          </button>
        }
      >
        <div className="flex items-start gap-2 text-sm text-ink">
          <AlertCircle className="w-4 h-4 text-critical mt-0.5 shrink-0" />
          <p>{t('designer.actions.publish_errors_intro')}</p>
        </div>
        {publishErrors ? (
          <div className="mt-3">
            <QualityFindingList findings={publishErrors} />
          </div>
        ) : null}
      </Dialog>

      {/* New version confirm */}
      <Dialog
        open={pending === 'newVersion'}
        onClose={() => !newVersionMutation.isPending && setPending(null)}
        title={t('designer.actions.new_version_dialog_title')}
        footer={
          <>
            <button
              type="button"
              onClick={() => setPending(null)}
              disabled={newVersionMutation.isPending}
              className="px-4 py-2 text-sm text-ink-muted hover:text-ink rounded-md disabled:opacity-50"
            >
              {t('designer.picker.cancel')}
            </button>
            <button
              type="button"
              onClick={() => newVersionMutation.mutate()}
              disabled={newVersionMutation.isPending}
              className="px-5 py-2 bg-brand hover:bg-brand/90 text-canvas text-sm font-medium rounded-md disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {newVersionMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : null}
              {t('designer.actions.new_version_dialog_confirm', {
                next: bumpMinor(version.version),
              })}
            </button>
          </>
        }
      >
        <p className="text-sm text-ink leading-relaxed">
          {t('designer.actions.new_version_dialog_body', {
            current: version.version,
            next: bumpMinor(version.version),
          })}
        </p>
      </Dialog>

      {/* Discard draft confirm */}
      <Dialog
        open={pending === 'discardDraft'}
        onClose={() => !discardDraftMutation.isPending && setPending(null)}
        title={t('designer.actions.discard_dialog_title', {
          version: version.version,
        })}
        footer={
          <>
            <button
              type="button"
              onClick={() => setPending(null)}
              disabled={discardDraftMutation.isPending}
              className="px-4 py-2 text-sm text-ink-muted hover:text-ink rounded-md disabled:opacity-50"
            >
              {t('designer.picker.cancel')}
            </button>
            <button
              type="button"
              onClick={() => discardDraftMutation.mutate()}
              disabled={discardDraftMutation.isPending}
              className="px-5 py-2 bg-critical hover:bg-critical/90 text-canvas text-sm font-medium rounded-md disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {discardDraftMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : null}
              {t('designer.actions.discard_dialog_confirm')}
            </button>
          </>
        }
      >
        <p className="text-sm text-ink leading-relaxed">
          {t('designer.actions.discard_dialog_body', {
            version: version.version,
          })}
        </p>
        <p className="mt-2 text-xs text-ink-muted leading-relaxed">
          {t('designer.actions.discard_dialog_hint')}
        </p>
      </Dialog>

      {/* Archive confirm */}
      <Dialog
        open={pending === 'archive'}
        onClose={() => !archiveMutation.isPending && setPending(null)}
        title={t('designer.actions.archive_dialog_title')}
        footer={
          <>
            <button
              type="button"
              onClick={() => setPending(null)}
              disabled={archiveMutation.isPending}
              className="px-4 py-2 text-sm text-ink-muted hover:text-ink rounded-md disabled:opacity-50"
            >
              {t('designer.picker.cancel')}
            </button>
            <button
              type="button"
              onClick={() => archiveMutation.mutate()}
              disabled={archiveMutation.isPending}
              className="px-5 py-2 bg-critical hover:bg-critical/90 text-canvas text-sm font-medium rounded-md disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {archiveMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : null}
              {t('designer.actions.archive_dialog_confirm')}
            </button>
          </>
        }
      >
        <p className="text-sm text-ink leading-relaxed">
          {t('designer.actions.archive_dialog_body', { name: template.name })}
        </p>
        <p className="mt-2 text-xs text-ink-muted leading-relaxed">
          {t('designer.actions.archive_dialog_hint')}
        </p>
      </Dialog>
    </>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function ActionButton({
  kind,
  icon,
  label,
  onClick,
}: {
  kind: 'primary' | 'ghost'
  icon: React.ReactNode
  label: string
  onClick: () => void
}) {
  const className =
    kind === 'primary'
      ? 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-brand text-canvas text-xs font-medium hover:bg-brand/90'
      : 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-line text-xs text-ink-muted hover:text-ink hover:bg-surface-muted'
  return (
    <button type="button" onClick={onClick} className={className}>
      {icon}
      {label}
    </button>
  )
}

/** Bump del minor de una versión semver. "1.0.0" → "1.1.0", "2.3.0" → "2.4.0".
 * Si el formato no matchea (raro), devuelve "<original>.1" como fallback. */
function bumpMinor(version: string): string {
  const m = /^(\d+)\.(\d+)\.(\d+)(.*)$/.exec(version)
  if (!m) return `${version}.1`
  const major = m[1]
  const minor = Number(m[2])
  // Bump minor, reset patch a 0, descartar pre-release.
  return `${major}.${minor + 1}.0`
}
